class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Delegated add-to-cart for the "Recommended For You" row. Bound to the
    // cart-drawer element (not the form) so it survives #CartDrawer-Main
    // re-renders; the submit event bubbles up from the injected form.
    this.addEventListener('submit', this.onRecommendationSubmit.bind(this));
  }

  onRecommendationSubmit(event) {
    const form = event.target && event.target.closest ? event.target.closest('.cart-recs__form') : null;
    if (!form) return;
    event.preventDefault();

    const button = form.querySelector('.cart-recs__button');
    if (button && button.getAttribute('aria-disabled') === 'true') return;
    if (button) {
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
    }

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(form);
    const sections = this.getSectionsToRender().map((section) => section.id);
    formData.append('sections', sections.join(','));
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    const addUrl = (typeof routes !== 'undefined' && routes.cart_add_url) || '/cart/add.js';

    fetch(addUrl, config)
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status) {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'cart-recommendations',
              productVariantId: formData.get('id'),
              errors: parsedState.errors || parsedState.description,
              message: parsedState.message,
            });
          }
          // Re-enable so the shopper can retry.
          if (button) {
            button.classList.remove('loading');
            button.removeAttribute('aria-disabled');
          }
          return;
        }

        // Publish BEFORE re-rendering, exactly like product-form.js does. The
        // drawer's <cart-drawer-items> (a CartItems subclass) subscribes to
        // cartUpdate and, for any source other than 'cart-items', self-refreshes
        // from the cart PAGE's main-cart-items section. Publishing first lets the
        // OUTGOING element handle it and then be discarded when we replace
        // #CartDrawer-Main; publishing after would make the freshly injected
        // element overwrite itself with the unstyled cart-page table (the bug).
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-recommendations',
            productVariantId: formData.get('id'),
            cartData: parsedState,
          });
        }

        // Same section injection + is-empty handling as renderContents, minus open().
        this.renderDrawerSections(parsedState);
      })
      .catch((e) => {
        console.error(e);
        // The button lives inside #CartDrawer-Main and may have been replaced by
        // the time this runs; guard the reset in case it wasn't.
        if (button && button.isConnected) {
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
        }
      });
  }

  // Shared section injection used by both renderContents() (full open) and the
  // recommendations add handler (in-place). Replaces the dynamic regions
  // (#CartDrawer-Main + #cart-icon-bubble) and clears the empty state — no
  // open()/overlay work here so callers can decide whether to open the drawer.
  renderDrawerSections(parsedState) {
    if (!parsedState || !parsedState.sections) return;

    this.classList.remove('is-empty');
    const inner = this.querySelector('.drawer__inner');
    if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');

    this.getSectionsToRender().forEach((section) => {
      const html = parsedState.sections[section.id];
      if (html == null) return;
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(html, section.selector);
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // Mutual exclusion: only one off-canvas drawer open at a time.
    const sideMenu = document.querySelector('side-menu');
    if (sideMenu && sideMenu.classList.contains('active')) sideMenu.close();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    // Only release the shared scroll lock if no other drawer (e.g. the side
    // menu) is still open, so closing one doesn't unlock the page behind another.
    if (!document.querySelector('side-menu.active, cart-drawer.active')) {
      document.body.classList.remove('overflow-hidden');
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.renderDrawerSections(parsedState);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        // Replace only the dynamic region; the express-checkout block sits
        // outside #CartDrawer-Main so its hydrated buttons survive re-renders.
        selector: '#CartDrawer-Main',
      },
      { id: 'cart-icon-bubble', selector: '#cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        // Dynamic region only (see CartDrawer.getSectionsToRender): keeps the
        // express-checkout block, which lives outside #CartDrawer-Main, intact.
        selector: '#CartDrawer-Main',
      },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '#cart-icon-bubble' },
    ];
  }

  // Refresh from the cart-drawer section (not the cart PAGE's main-cart-items)
  // so the drawer's own markup is used. Mirrors refreshCartDrawerFromSection()
  // in cart.js; replaces only the items container, guarding for missing nodes.
  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const source = html.querySelector('#CartDrawer-CartItems');
        const destination = document.querySelector('#CartDrawer-CartItems');
        if (source && destination) destination.innerHTML = source.innerHTML;
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
